import { observer } from "mobx-react";
import { DefaultModalLayout } from "../../../../components/modal/layouts/default-modal-layout";
import Textbanner from "../text-banner/text-banner";
import FontHeading1 from "../../../../components/fonts/font-heading1/font-heading1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../components/fonts/font-props";
import { BUTTON_VARIANTS } from "../../../../components/inputs/button-variants";
import './set-up-now-modal.scss';
import FontHeading2 from "../../../../components/fonts/font-heading2/font-heading2";
import FontBody1 from "../../../../components/fonts/font-body1/font-body1";
import shipByReturngoPageStore from "../../ship-by-returngo-page-store";
import Card from "../../../../components/layouts/card/card";
import ListLayout from "../../../../components/layouts/list-layout/list-layout";
import { RETURN_METHOD_CATEGORY } from "../../../return-methods/return-method-types";
import Table from "../../../../components/table/table";
import TableRow from "../../../../components/table/components/table-row/table-row";
import { Checkbox, CHECKBOX_SIZES } from "../../../../components/inputs/checkbox/checkbox";
import { CARD_SPACING_VARIANT } from "../../../../components/layouts/card/card.types";
import useTranslation from "../../../../translation/hooks/translation-hook";
import { TABLE_SPACING_VARIANT } from "../../../../components/table/table.types";
import thankYouBg from '../../../../images/sbr-promotion-page-thank-you-bg.svg';

interface ISetupNowModalProps {
    onCloseClicked: () => void;
    showSetUpNowSuccessModal: () => void;
}

const SetupNowModal: React.FC<ISetupNowModalProps> = observer(({ onCloseClicked, showSetUpNowSuccessModal }) => {
    const {getTranslation} = useTranslation();

    const onUpdateReturnMethodsClicked = async () => {
        shipByReturngoPageStore.convertRMsToSBR(onCloseClicked, showSetUpNowSuccessModal, getTranslation);
    };

    /** refactor this, move into store from return-methods-list.tsx and do not define in two places */
    const textByCategoryMap = {
        [RETURN_METHOD_CATEGORY.NO_SHIPMENT_REQUIRED]: {
            title: 'No shipment required',
        },
        [RETURN_METHOD_CATEGORY.DROP_OFF_LOCATION]: {
            title: 'Return to drop-off location',
        },
        [RETURN_METHOD_CATEGORY.SHIP_WITH_ANY_CARRIER]: {
            title: 'Ship with any carrier',
        },
        [RETURN_METHOD_CATEGORY.SHIP_WITH_PRE_PAID_LABEL]: {
            title: 'Ship with pre-paid label',
        },
    };

    const returnMethodCategories = shipByReturngoPageStore.returnMethodCategories.filter(rmCategory => rmCategory.list.length > 0);

    return (
        <DefaultModalLayout
            isCloseDisabled={shipByReturngoPageStore.isLoading || shipByReturngoPageStore.isUpdating}
            onCloseClicked={onCloseClicked}
            buttons={[
                {
                    text: 'Cancel',
                    variant: BUTTON_VARIANTS.Tertiary,
                    onClick: onCloseClicked,
                    hasLoader: false,
                    disabled: shipByReturngoPageStore.isUpdating
                },
                {
                    text: 'Update Methods',
                    variant: BUTTON_VARIANTS.PrimaryAccent,
                    onClick: onUpdateReturnMethodsClicked,
                    hasLoader: true,
                    isLoading: shipByReturngoPageStore.isUpdating,
                    disabled: shipByReturngoPageStore.selectedReturnMethods.length === 0 || shipByReturngoPageStore.isUpdating
                }
            ]}
        >
            <div className={'sbr-page-set-up-now-modal'}>
                <Textbanner backgroundImage={thankYouBg}>
                    <div className="set-up-now-banner-content">
                        <FontHeading1 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.NEUTRAL_WHITE}>
                            Thank you for choosing Ship by ReturnGO
                        </FontHeading1>
                    </div>
                </Textbanner>

                <div className="set-up-now">
                    <FontHeading2 variant={FONT_VARIANT.BOLD}>Switch Return Methods to Ship by ReturnGO</FontHeading2>
                    <FontBody1>To start using Ship by ReturnGO, the shipping connection for the return methods listed below will be switched to Ship by ReturnGO. Please review the return methods that will be updated.</FontBody1>
                </div>

                <ListLayout>
                    {returnMethodCategories.map(rmCategory => {
                        return (
                            <Card
                                key={rmCategory.category}
                                toolbar={{
                                    title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{`Affected ${textByCategoryMap[rmCategory.category].title} Return Methods`}</FontBody1>,
                                }}
                                isExpandable={true}
                                isExpanded={false}
                                spacingVariant={CARD_SPACING_VARIANT.SMAll}
                                expandCallback={()=>{}}
                            >
                                <Table
                                    spacingVariant={TABLE_SPACING_VARIANT.SMAll}
                                    columns={[
                                        { content: "Internal Name", width: { value: 3, type: "fr" } },
                                        { content: "Linked Policy Rules", width: { value: 1, type: "fr" } },
                                        { content: "Current Shipping Connection", width: { value: 2, type: "fr" } },
                                    ]}
                                >
                                    {rmCategory.list.map((returnMethod) => (
                                        <TableRow
                                            key={returnMethod.id}
                                            cells={[
                                                <Checkbox onChange={() => shipByReturngoPageStore.toggleRmSelected(returnMethod)} checked={shipByReturngoPageStore.isRmSelected(returnMethod)} label={getTranslation(returnMethod.name, 'en')} size={CHECKBOX_SIZES.SMALL} />,
                                                returnMethod.policy_rules_count,
                                                shipByReturngoPageStore.getCurrentShippingConnection(returnMethod),
                                            ]}
                                        />
                                    ))}
                                </Table>
                            </Card>
                        );
                    })}
                </ListLayout>
            </div>
        </DefaultModalLayout>
    );
});

export default SetupNowModal;